import React from "react";
import { Link } from "react-router-dom";

/**
 * AbilityTestContent — “Certificate-style” proficiency test cards
 * - 🔒 Exactly 3 cards per row and 4 rows total (12 cards)
 * - Uses fixed Bootstrap cols (col-4) so it’s always 3-up across breakpoints
 * - Certificate aesthetic (double border, seal, watermark)
 */

const LOGO_WATERMARK = "/images/brand/techventurellc-mark.png"; // update to your logo path

const TESTS = [
  { title: "Java", to: "/javaQuiz", summary: "OOP & inheritance, exceptions, generics, collections, I/O, concurrency, JVM memory model, best practices.", updated: "3 days ago" },
  { title: "JavaScript", to: "/javascriptQuiz", summary: "Scope & closures, this, prototypes, async/await & promises, event loop, modules, DOM, performance.", updated: "3 days ago" },
  { title: "SQL", to: "/SQLQuiz", summary: "SELECT/JOIN/GROUP BY, window functions, normalization, indexes & query plans, transactions & isolation.", updated: "3 days ago" },
  { title: "Collection API", to: "/collectionApiQuiz", summary: "List/Set/Map semantics, iterators, equals/hashCode, comparators, concurrency-safe collections, perf trade-offs.", updated: "3 days ago" },
  { title: "Spring", to: "/springQuiz", summary: "IoC/DI, bean lifecycle, MVC & REST, AOP, Data/JPA, transactions, Boot autoconfig, validation & testing.", updated: "3 days ago" },
  { title: "Hibernate", to: "/hibernateQuiz", summary: "Entity mapping, relationships/cascades, fetching, JPQL/Criteria, caching, transactions, tuning & pitfalls.", updated: "3 days ago" },
  { title: "React", to: "/reactJSQuiz", summary: "JSX, state/props, hooks (useState/useEffect/useMemo), context, reconciliation, performance, testing.", updated: "3 days ago" },
  { title: "Angular", to: "/angularQuiz", summary: "Components/templates, DI, forms, RxJS/observables, routing/guards, change detection, testing.", updated: "3 days ago" },
  { title: "Java 8 Features", to: "/java8Quiz", summary: "Lambdas, functional interfaces, streams, Optional, Date/Time API, default/static methods, method refs.", updated: "3 days ago" },
  { title: "Python", to: "/pythonProficiencyTest", summary: "Data model, comprehensions, iterators/generators, OOP/dataclasses, typing, stdlib, venv, asyncio.", updated: "2 days ago" },
  { title: "Kafka", to: "/kafkaProficiencyTest", summary: "Topics/partitions, producers/consumers, delivery semantics, retention/compaction, schema, Streams/KSQL.", updated: "2 days ago" },
  { title: "Cassandra", to: "/cassandraProficiencyTest", summary: "Query-first modeling, partition/clustering keys, replication/CL, compaction/TTL, LWT, drivers & tuning.", updated: "2 days ago" },
];

// Chunk into groups of 3 (exactly 4 rows for 12 items)
const chunk3 = (arr) => {
  const out = [];
  for (let i = 0; i < arr.length; i += 3) out.push(arr.slice(i, i + 3));
  return out;
};

const CertificateCard = ({ item }) => {
  return (
    <Link
      to={item.to}
      className="text-reset text-decoration-none d-block h-100"
      aria-label={`Open ${item.title} Proficiency Certificate Exam`}
    >
      <article
        className="certificate-card h-100"
        style={{
          backgroundImage: `radial-gradient(1200px 400px at -5% -20%, rgba(99,102,241,.06), transparent 60%), radial-gradient(900px 320px at 110% 10%, rgba(34,197,94,.05), transparent 60%), url('${LOGO_WATERMARK}')`,
        }}
      >
        <div className="certificate-inner">
          <header className="certificate-header">
            <div className="certificate-kicker" aria-hidden="true">
              Certificate Examination
            </div>
            <h3 className="certificate-title">{item.title}</h3>
            <div className="certificate-issued">Issued by Tech Venture LLC</div>
          </header>

          <div className="certificate-body">
            <p className="certificate-summary">{item.summary}</p>
            <ul className="certificate-meta list-inline m-0">
              <li className="list-inline-item">🕑 10 minutes</li>
              <li className="list-inline-item">🧩 20 questions</li>
              <li className="list-inline-item">🏅 Shareable certificate</li>
            </ul>
          </div>

          <footer className="certificate-footer">
            <span className="certificate-updated">Updated {item.updated}</span>
            <span className="certificate-cta" aria-hidden="true">
              Start Exam →
            </span>
          </footer>

          {/* Rosette/Seal */}
          <div className="certificate-seal" aria-hidden="true">
            <div className="seal-inner">
              <div className="seal-top">PRO</div>
              <div className="seal-mid">TV</div>
              <div className="seal-bot">CERT</div>
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
};

const AbilityTestContent = () => {
  const rows = chunk3(TESTS); // will produce 4 rows for 12 items

  return (
    <div className="ability-tests-page">
      {/* Inline styles for the certificate aesthetic */}
      <style>{`
        .certificate-card {
          position: relative;
          border-radius: 18px;
          background-color: #fff;
          background-repeat: no-repeat;
          background-position: center;
          background-size: 360px auto, 360px auto, 240px auto; /* last is watermark */
          box-shadow: 0 6px 24px rgba(16,24,40,.06);
          transition: transform .2s ease, box-shadow .2s ease;
          isolation: isolate;
        }
        .certificate-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 28px rgba(16,24,40,.10);
        }
        /* Double-frame certificate border */
        .certificate-inner {
          position: relative;
          height: 100%;
          padding: 20px 20px 18px;
          border: 2px solid #e5e7eb;         /* outer */
          border-radius: 16px;
          background:
            linear-gradient(#fff,#fff) padding-box,
            repeating-linear-gradient(90deg,#d1d5db 0 2px, transparent 2px 6px) border-box;
          box-shadow: inset 0 0 0 6px #f3f4f6; /* inner mat */
        }
        /* Corner ornaments */
        .certificate-inner:before,
        .certificate-inner:after {
          content: "";
          position: absolute;
          inset: 10px;
          border: 1px dashed rgba(17,24,39,.12);
          border-radius: 12px;
          pointer-events: none;
        }

        .certificate-header { text-align: center; margin-bottom: 10px; }
        .certificate-kicker { font-variant: small-caps; letter-spacing: .12em; color: #64748b; font-size: .8rem; }
        .certificate-title { font-size: 1.5rem; margin: 4px 0 2px; font-weight: 700; letter-spacing: .02em; }
        .certificate-issued { font-size: .85rem; color: #475569; }
        .certificate-body { margin-top: 6px; text-align: center; padding: 0 6px; }
        .certificate-summary { color: #334155; font-size: .95rem; margin-bottom: 10px; }
        .certificate-meta { color: #64748b; font-size: .85rem; }
        .certificate-footer { margin-top: 14px; display: flex; align-items: center; justify-content: space-between; color: #64748b; font-size: .8rem; }
        .certificate-cta { font-weight: 600; color: #2563eb; }

        /* Gold rosette seal */
        .certificate-seal {
          position: absolute; top: -14px; right: -14px; width: 96px; height: 96px;
          border-radius: 50%;
          background: radial-gradient(circle at 30% 30%, #fde68a, #f59e0b);
          box-shadow: 0 4px 10px rgba(0,0,0,.18);
          display: grid; place-items: center; transform: rotate(8deg); z-index: 1;
        }
        .certificate-seal:before { content: ""; position: absolute; inset: 10px; border-radius: 50%; border: 2px dashed rgba(0,0,0,.2); }
        .seal-inner { color: #111827; text-align: center; line-height: 1; font-weight: 800; letter-spacing: .06em; text-shadow: 0 1px 0 rgba(255,255,255,.4); }
        .seal-top { font-size: .6rem; } .seal-mid { font-size: 1.2rem; } .seal-bot { font-size: .6rem; }

        /* Small screens: make the seal smaller */
        @media (max-width: 576px) {
          .certificate-seal { width: 76px; height: 76px; top: -10px; right: -10px; }
        }
      `}</style>

      {/* ---------- HERO / BANNER ---------- */}
      <section
        className="py-5 py-lg-6 bg-light position-relative overflow-hidden"
        aria-label="Proficiency Tests and Certifications Banner"
        style={{
          background:
            "radial-gradient(1200px 400px at -5% -20%, rgba(99,102,241,.15), transparent 60%), radial-gradient(900px 320px at 110% 10%, rgba(34,197,94,.12), transparent 60%)",
        }}
      >
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-12 col-lg-7">
              <h1 className="display-6 fw-bold mb-2">
                Proficiency Tests &amp; Verifiable Certificates
              </h1>
              <p className="lead text-muted mb-3">
                Practice with curated, time-boxed quizzes. Score well to unlock a shareable certificate—perfect for LinkedIn.
              </p>
              <ul className="list-inline small text-muted mb-4">
                <li className="list-inline-item me-3">🕑 10 minutes each</li>
                <li className="list-inline-item me-3">🧩 20 questions</li>
                <li className="list-inline-item">🏅 Shareable certificate</li>
              </ul>
              <div className="d-flex gap-2 flex-wrap">
                <Link to="/badges" className="btn btn-primary">How Certificates Work</Link>
                <a href="/codingRound" className="btn btn-outline-secondary">Practice Coding Challenges</a>
              </div>
            </div>
            <div className="col-12 col-lg-5">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h2 className="h5 mb-2">What’s covered?</h2>
                  <p className="text-muted mb-2">Each test focuses on fundamentals interviewers care about:</p>
                  <ul className="small text-muted mb-0">
                    <li>Core language/framework concepts</li>
                    <li>Real-world scenarios &amp; gotchas</li>
                    <li>Best practices, performance &amp; debugging</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- GRID: EXACTLY 3 PER ROW, 4 ROWS ---------- */}
      <div className="container py-5">
        <p className="lead text-muted mb-3">Choose a stack to take the exam!</p>

        {rows.map((group, idx) => (
          <div className="row g-4 mb-4" key={`row-${idx}`}>
            {group.map((t) => (
              <div className="col-4 d-flex" key={t.title}>
                <CertificateCard item={t} />
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* ---------- MOBILE HELP CTA ---------- */}
      <div className="container pb-5">
        <div className="alert alert-primary" role="note">
          Tip: Each certificate is fully tappable—just tap a topic to start the exam.
        </div>
      </div>
    </div>
  );
};

export default AbilityTestContent;
